import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select'
import { showError, showSuccess } from '@/lib/swal'
import { useAuthStore } from '@/store/auth.store'
import { purchaseRequisitionService } from '@/services/purchaseRequisitionService'
import { itemRequestApiService } from '@/services/itemRequestApiService'
import type { ItemRequestVm } from '@/api/types'

function todayIso() {
  const d = new Date()
  return d.toISOString().slice(0, 10)
}

function dateOnlyToIsoZ(dateOnly: string): string {
  // Convert YYYY-MM-DD to ISO datetime string with timezone (Z)
  // Example: 2026-04-23 -> 2026-04-23T00:00:00.000Z
  const d = new Date(`${dateOnly}T00:00:00.000Z`)
  return d.toISOString()
}

type RequestItemOption = { id: number; name: string }

export const ItemRequestDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = Number(user?.selectedBranch?.branchId ?? 0)

  const isNew = params.id === 'new' || !params.id
  const editId = !isNew ? Number(params.id) : 0

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [items, setItems] = useState<RequestItemOption[]>([])

  const [selectedItemId, setSelectedItemId] = useState<number>(0)
  const [requiredDate, setRequiredDate] = useState<string>(todayIso())
  const [quantity, setQuantity] = useState<string>('1')
  const [details, setDetails] = useState<string>('')
  const [status, setStatus] = useState<number>(0)
  const [approvalBy, setApprovalBy] = useState<string>('')
  const [approvalDate, setApprovalDate] = useState<string>('')

  const req = t('itemRequestPage.requiredMark')

  useEffect(() => {
    if (!branchId) return
    let cancelled = false
    setLoading(true)
    void purchaseRequisitionService
      .getItems(branchId)
      .then((res) => {
        if (cancelled) return
        setItems(res || [])
      })
      .catch((e: any) => {
        if (!cancelled) toast.error(e?.message ?? t('itemRequestPage.loadItemsFailed'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [branchId, t])

  useEffect(() => {
    if (isNew) return
    if (!branchId || !editId) return
    let cancelled = false
    setLoading(true)
    void itemRequestApiService
      .getById(editId, branchId)
      .then((vm) => {
        if (cancelled) return
        if (!vm) {
          toast.error(t('itemRequestPage.loadFailed'))
          navigate('/self-services/item-requests')
          return
        }
        setSelectedItemId(vm.itemId ?? 0)
        setRequiredDate(String(vm.requestedDate || '').slice(0, 10) || todayIso())
        setQuantity(String(vm.quantity ?? 1))
        setDetails(vm.details ?? '')
        setStatus(Number(vm.status ?? 0))
        setApprovalBy(vm.approvalBy ?? '')
        setApprovalDate(vm.approvalDate ? String(vm.approvalDate).slice(0, 19) : '')
      })
      .catch((e: any) => {
        if (!cancelled) toast.error(e?.response?.data?.message ?? e?.message ?? t('itemRequestPage.loadFailed'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isNew, editId, branchId, navigate, t])

  const itemOptions = useMemo(() => {
    const opts: SearchableSelectOption[] = [
      { value: '0', label: t('itemRequestPage.placeholderSelect') },
      ...items
        .filter((it) => (it.id ?? 0) > 0)
        .map((it) => ({
          value: String(it.id),
          label: it.name,
        })),
    ]
    return opts
  }, [items, t])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!branchId) return
    if (!selectedItemId) {
      await showError(t('itemRequestPage.saveFailedTitle'), t('itemRequestPage.toastItemRequired'))
      return
    }
    if (!requiredDate) {
      await showError(t('itemRequestPage.saveFailedTitle'), t('itemRequestPage.toastRequiredDate'))
      return
    }
    const qty = Number(quantity || 0)
    if (!Number.isFinite(qty) || qty <= 0) {
      await showError(t('itemRequestPage.saveFailedTitle'), t('itemRequestPage.toastQuantity'))
      return
    }
    if (!details.trim()) {
      await showError(t('itemRequestPage.saveFailedTitle'), t('itemRequestPage.toastDetails'))
      return
    }

    try {
      setSaving(true)

      // Validate inventory quantity before save
      const available = await itemRequestApiService.getAvailableQuantityForEmployeeDepartment({
        itemId: selectedItemId,
        branchId,
        departmentId: 0,
      })
      if (qty > available) {
        await showError(t('itemRequestPage.saveFailedTitle'), t('itemRequestPage.toastInsufficientInventory', { available }))
        return
      }

      const vm: ItemRequestVm = {
        id: isNew ? 0 : editId,
        empId: 0,
        itemId: selectedItemId,
        departmentId: 0,
        quantity: qty,
        requestedDate: dateOnlyToIsoZ(requiredDate),
        details,
        status: isNew ? 0 : Number(status || 0),
        approvalBy: approvalBy || undefined,
        approvalDate: approvalDate ? new Date(approvalDate).toISOString() : isNew ? new Date().toISOString() : undefined,
        branchId,
        isEditable: 0,
      }

      if (isNew) await itemRequestApiService.save(vm)
      else await itemRequestApiService.update(vm)

      await showSuccess(
        isNew ? t('itemRequestPage.savedTitle') : t('itemRequestPage.updatedTitle'),
        isNew ? t('itemRequestPage.savedMessage') : t('itemRequestPage.updatedMessage'),
        2500,
      )
      navigate('/self-services/item-requests')
    } catch (err: any) {
      await showError(
        isNew ? t('itemRequestPage.saveFailedTitle') : t('itemRequestPage.updateFailedTitle'),
        err?.response?.data?.message ?? err?.message ?? t('itemRequestPage.saveFailed'),
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader title={t('itemRequestPage.pageTitle')} description={t('itemRequestPage.pageDescription')} />

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="p-6 space-y-6 overflow-visible">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                {!isNew ? (
                  <div className="text-sm text-muted-foreground">
                    {t('itemRequestPage.requestNo')}: <span className="font-medium text-foreground">{editId || '—'}</span>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <SearchableSelect
                  label={`${req} ${t('itemRequestPage.labelItem')}`}
                  options={itemOptions}
                  value={selectedItemId ? String(selectedItemId) : '0'}
                  onValueChange={(v) => setSelectedItemId(Number(v) || 0)}
                  placeholder={t('itemRequestPage.placeholderSelect')}
                  searchPlaceholder={t('itemRequestPage.searchItem')}
                  emptyMessage={t('itemRequestPage.emptyItem')}
                />
              </div>

              <div className="space-y-2">
                <Label>
                  {req} {t('itemRequestPage.labelRequiredDate')}
                </Label>
                <Input type="date" value={requiredDate} onChange={(e) => setRequiredDate(e.target.value)} disabled={loading} />
              </div>

              <div className="space-y-2">
                <Label>
                  {req} {t('itemRequestPage.labelQuantity')}
                </Label>
                <Input value={quantity} onChange={(e) => setQuantity(e.target.value)} disabled={saving} />
              </div>

              {!isNew ? (
                <div className="space-y-2">
                  <Label>{t('itemRequestPage.labelStatus')}</Label>
                  <select
                    className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={String(status)}
                    onChange={(e) => setStatus(Number(e.target.value) || 0)}
                    disabled={saving}
                  >
                    <option value="0">{t('itemRequestPage.statusPending')}</option>
                    <option value="1">{t('itemRequestPage.statusApproved')}</option>
                    <option value="2">{t('itemRequestPage.statusRejected')}</option>
                  </select>
                </div>
              ) : null}

              {!isNew ? (
                <div className="space-y-2">
                  <Label>{t('itemRequestPage.labelApprovedBy')}</Label>
                  <Input value={approvalBy} onChange={(e) => setApprovalBy(e.target.value)} disabled={saving} />
                </div>
              ) : null}

              {!isNew ? (
                <div className="space-y-2">
                  <Label>{t('itemRequestPage.labelApprovalDate')}</Label>
                  <Input value={approvalDate} onChange={(e) => setApprovalDate(e.target.value)} disabled={saving} placeholder="YYYY-MM-DDTHH:mm:ss" />
                </div>
              ) : null}

              <div className="space-y-2 md:col-span-2">
                <Label>
                  {req} {t('itemRequestPage.labelDetails')}
                </Label>
                <Textarea
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder={t('itemRequestPage.placeholderDetails')}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" disabled={saving} onClick={() => navigate('/self-services/item-requests')}>
                {t('itemRequestPage.cancel')}
              </Button>
              <Button type="submit" disabled={saving || loading}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? t('itemRequestPage.saving') : isNew ? t('itemRequestPage.save') : t('itemRequestPage.update')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}

